import React, { type ReactElement } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { mapValues, uniq } from 'lodash';
import {
  DelimitedNumericArrayParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault
} from 'use-query-params';
import Card from 'atom-components/card';
import TablePagination from 'atom-components/table-pagination';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import {
  defaultPage,
  defaultPaginationFilters,
  generatePaginationFilters
} from 'helpers/pagination.helpers';
import { toArrayOfNumbers } from 'helpers/query-params.helpers';
import {
  defaultSortingFilter,
  fromSortingState,
  isColumnSorted,
  sortDirection,
  toSortingState
} from 'helpers/sorting.helpers';
import { firstTimeInYear, lastTimeInYear } from 'helpers/year.helpers';
import {
  organisationUsersWithMarkerRoleParams,
  organisationUsersWithMarkingAllocationRoleParams
} from 'pages/marking/helpers/request-params.helpers';
import MarkingPageLayout from 'pages/marking/layouts/marking-page-layout';
import FilterForm from 'pages/marking/subpages/marks-for-review-tab/filter-form';
import type { FilterFormValues } from 'pages/marking/subpages/marks-for-review-tab/filter-form/filter-form.schema';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import { useTestInstanceResponseMarks } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import {
  MarkStatus,
  type TestInstanceResponseMarkWithActiveMarkerAndResponse
} from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useTestInstanceResponses } from 'services/assist-api/entities/test-instance-response/test-instance-response';
import type { BaseTestInstanceResponse } from 'services/assist-api/entities/test-instance-response/types';
import {
  type TestInstanceUserWithCustomerUserAndResponses,
  useTestInstanceUsersNoDefaults
} from 'services/assist-api/entities/test-instance-user/test-instance-users';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';
import MarksForReviewTable from './marks-for-review-table';

const defaultTableFilters = {
  nsn: withDefault(StringParam, ''),
  responseId: withDefault(StringParam, ''),
  markId: withDefault(StringParam, ''),
  markerOrganisationUserIds: withDefault(DelimitedNumericArrayParam, [])
};

export default function MarksForReviewTab(): ReactElement {
  const [tableFilters, setTableFilters] = useQueryParams(defaultTableFilters);
  const [sortingFilter, setSortingFilter] = useQueryParam('sorting', defaultSortingFilter);
  const [paginationFilters, setPaginationFilters] = useQueryParams(defaultPaginationFilters);

  // Get the current user's organisation from the cache. We should have an earlier check to ensure
  // that the user has joined and is switched to an organisation by this point:
  const currentOrganisation = useAuthCustomer();

  function generateMarksForReviewFilters(): EntityFilters {
    const filters: EntityFilters = {};

    // Start table row filters.
    if (tableFilters.nsn) {
      filters['testInstanceResponse.user.user.identifier'] = tableFilters.nsn;
    }
    if (tableFilters.responseId) {
      filters.testInstanceResponse = tableFilters.responseId;
    }
    if (tableFilters.markId) {
      filters.id = tableFilters.markId;
    }
    if (tableFilters.markerOrganisationUserIds.length > 0) {
      filters['activeMarker.user'] = toArrayOfNumbers(tableFilters.markerOrganisationUserIds);
    }
    // End table row filters.

    // Start column sorting filters.
    if (isColumnSorted('nsn', sortingFilter)) {
      filters['order[testInstanceResponse.user.user.identifier]'] = sortDirection(
        'nsn',
        sortingFilter
      );
    }
    if (isColumnSorted('responseId', sortingFilter)) {
      filters['order[testInstanceResponse.id]'] = sortDirection('responseId', sortingFilter);
    }
    if (isColumnSorted('markId', sortingFilter)) {
      filters['order[id]'] = sortDirection('markId', sortingFilter);
    }
    // Sort by username (displayed in tooltip), since we can't reliably sort by full name:
    if (isColumnSorted('marker', sortingFilter)) {
      filters['order[activeMarker.user.user.username]'] = sortDirection('marker', sortingFilter);
    }
    if (isColumnSorted('reviewer', sortingFilter)) {
      filters['order[activeReviewer.user.username]'] = sortDirection('reviewer', sortingFilter);
    }
    if (isColumnSorted('status', sortingFilter)) {
      filters['order[status]'] = sortDirection('status', sortingFilter);
    }
    // End column sorting filters.

    return filters;
  }

  const currentYear = new Date().getFullYear().toString();
  const testInstanceResponseMarksQuery =
    useTestInstanceResponseMarks<TestInstanceResponseMarkWithActiveMarkerAndResponse>(
      {
        filters: {
          status: [MarkStatus.NeedsReview, MarkStatus.InReview, MarkStatus.FurtherEscalated],
          'testInstanceResponse.testInstance.customer': currentOrganisation?.id,
          'testInstanceResponse.testInstance.startDate[after]': firstTimeInYear(currentYear),
          'testInstanceResponse.testInstance.startDate[before]': lastTimeInYear(currentYear),
          ...generatePaginationFilters(paginationFilters.pageSize, paginationFilters.page),
          ...generateMarksForReviewFilters()
        },
        groups: [
          'id',
          'testinstanceresponsemark-testinstanceresponse',
          'testinstanceresponsemark-activemarker',
          'testinstanceresponsemark-activereviewer'
        ]
      },
      {
        enabled:
          !!(paginationFilters?.pageSize && paginationFilters?.page) && !!currentOrganisation?.id
      }
    );

  // Get existing active markers associated with each mark:
  const activeMarkerIds =
    uniq(testInstanceResponseMarksQuery.data?.json.map((m) => m.activeMarker?.id)) || [];
  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      {
        filters: { id: activeMarkerIds },
        groups: ['id', 'testinstanceresponsemarker-user', 'customersusers-user'],
        include: 'user'
      },
      { enabled: !!activeMarkerIds.length }
    );

  // Get the organisation users linked to each active marker:
  const activeOrganisationUserIds =
    uniq(testInstanceResponseMarkersQuery.data?.json.map((mkr) => mkr.user.id)) || [];
  const markerOrganisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkerRoleParams(currentOrganisation?.id, activeOrganisationUserIds),
    { enabled: !!activeOrganisationUserIds.length }
  );

  // Get existing active reviewers associated with each mark:
  const activeReviewerOrganisationUserIds: number[] =
    uniq(
      testInstanceResponseMarksQuery.data?.json
        .map((m) => m.activeReviewer?.id)
        .filter((id) => id !== undefined)
    ) || [];

  // Get the organisation users linked to each active reviewer:
  const reviewerOrganisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkingAllocationRoleParams(
      currentOrganisation?.id,
      activeReviewerOrganisationUserIds
    ),
    { enabled: !!activeReviewerOrganisationUserIds?.length }
  );

  // We shouldn't run the learner queries if there are no responses as we never want to fetch all
  // responses. This can happen in the event that there is an invalid filter that results in no
  // marks being returned.
  const responseIds: number[] =
    testInstanceResponseMarksQuery.data?.json.map((mark) => mark.testInstanceResponse.id) || [];
  const learnerTestInstanceResponsesQuery = useTestInstanceResponses<BaseTestInstanceResponse>(
    { filters: { id: responseIds } },
    { enabled: !!responseIds.length } // It is dangerous to fetch all responses here.
  );

  // Provides extra information about the learner (such as NSN) that can be used to enhance the
  // table. We are not currently requring this to be populated in order to render the table; it is
  // additional information that is used to enhance the table.
  const learnerOrganisationUserIds =
    learnerTestInstanceResponsesQuery.data?.json.map((r) => r.user.id) || [];
  const learnerTestInstanceUsersQuery =
    useTestInstanceUsersNoDefaults<TestInstanceUserWithCustomerUserAndResponses>(
      {
        filters: { id: learnerOrganisationUserIds },
        groups: ['customersusers-identifier', 'userstestinstances-responses'],
        include: 'user'
      },
      { enabled: !!learnerOrganisationUserIds.length }
    );

  // Note: We don't require the learner queries to be run for performance reasons:
  const isLoading = isInitialLoad(
    testInstanceResponseMarksQuery,
    testInstanceResponseMarkersQuery,
    markerOrganisationUsersQuery,
    reviewerOrganisationUsersQuery
  );

  const isError =
    testInstanceResponseMarksQuery.isError ||
    testInstanceResponseMarkersQuery.isError ||
    markerOrganisationUsersQuery.isError ||
    reviewerOrganisationUsersQuery.isError;

  return (
    <MarkingPageLayout>
      <Card>
        <Card.Header>
          <Card.Heading level={3}>Marks to Review</Card.Heading>
        </Card.Header>
        <Card.Body>
          <FilterForm
            defaultValuesFromURLParams={{
              nsn: tableFilters.nsn,
              responseId: tableFilters.responseId,
              markId: tableFilters.markId,
              markerOrganisationUserIds: toArrayOfNumbers(tableFilters.markerOrganisationUserIds)
            }}
            onSubmit={(formValues: FilterFormValues): void => {
              setTableFilters({ ...formValues });
              setPaginationFilters({ page: defaultPage });
            }}
            onReset={(): void => {
              setTableFilters(mapValues(defaultTableFilters, () => undefined));
              setPaginationFilters({ page: defaultPage });
            }}
          />
          {!isLoading && isError && (
            <LoadingErrorAlert
              queries={[
                testInstanceResponseMarksQuery,
                testInstanceResponseMarkersQuery,
                markerOrganisationUsersQuery,
                reviewerOrganisationUsersQuery
              ]}
            />
          )}
          <MarksForReviewTable
            isLoading={isLoading}
            marks={testInstanceResponseMarksQuery.data?.json || []}
            markers={testInstanceResponseMarkersQuery.data?.json || []}
            markerOrganisationUsers={markerOrganisationUsersQuery.data?.json || []}
            learnerTestInstanceUsers={learnerTestInstanceUsersQuery.data?.json || []} // Can be empty.
            reviewerOrganisationUsers={reviewerOrganisationUsersQuery.data?.json || []}
            initialSorting={toSortingState(sortingFilter)}
            onSort={(sorting: SortingState): void => setSortingFilter(fromSortingState(sorting))}
          />
        </Card.Body>
        <Card.Footer>
          <TablePagination
            query={testInstanceResponseMarksQuery}
            onChange={({ pageSize, page }): void => setPaginationFilters({ pageSize, page })}
          />
        </Card.Footer>
      </Card>
    </MarkingPageLayout>
  );
}
